import React, { useState, useEffect } from 'react';
import {
  Supply,
  CreateSupplyDto,
  UpdateSupplyDto,
  SupplyType,
  SupplyUnit,
  getSupplyTypeLabel,
  getSupplyUnitLabel,
} from '../api/supplies';
import AmountInput from './ui/AmountInput';

interface SupplyFormProps {
  supply?: Supply;
  onSubmit: (data: CreateSupplyDto | UpdateSupplyDto) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const SupplyForm: React.FC<SupplyFormProps> = ({
  supply,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CreateSupplyDto>({
    name: '',
    type: SupplyType.OTHER,
    quantity: 0,
    unit: SupplyUnit.PIECES,
    unitPrice: 0,
    minimumQuantity: 0,
    reorderPoint: 0,
    averageDailyUsage: 0,
    leadTimeDays: 7,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (supply) {
      setFormData({
        name: supply.name,
        type: supply.type,
        quantity: supply.quantity,
        unit: supply.unit,
        unitPrice: supply.unitPrice,
        purchaseDate: supply.purchaseDate,
        expirationDate: supply.expirationDate,
        supplier: supply.supplier || '',
        batchNumber: supply.batchNumber || '',
        location: supply.location || '',
        description: supply.description || '',
        instructions: supply.instructions || '',
        storageConditions: supply.storageConditions || '',
        minimumQuantity: supply.minimumQuantity,
        reorderPoint: supply.reorderPoint,
        maxStockLevel: supply.maxStockLevel,
        averageDailyUsage: supply.averageDailyUsage,
        leadTimeDays: supply.leadTimeDays,
        notes: supply.notes || '',
        metadata: supply.metadata,
      });
    }
  }, [supply]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.quantity < 0) {
      newErrors.quantity = 'Quantity cannot be negative';
    }

    if (formData.unitPrice < 0) {
      newErrors.unitPrice = 'Unit price cannot be negative';
    }

    if (formData.minimumQuantity < 0) {
      newErrors.minimumQuantity = 'Minimum quantity cannot be negative';
    }

    if (formData.reorderPoint < 0) {
      newErrors.reorderPoint = 'Reorder point cannot be negative';
    }

    if (formData.averageDailyUsage && formData.averageDailyUsage < 0) {
      newErrors.averageDailyUsage = 'Average daily usage cannot be negative';
    }

    if (formData.leadTimeDays && formData.leadTimeDays < 0) {
      newErrors.leadTimeDays = 'Lead time cannot be negative';
    }

    if (formData.maxStockLevel && formData.maxStockLevel < formData.reorderPoint) {
      newErrors.maxStockLevel = 'Max stock level must be greater than reorder point';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
               type === 'date' ? (value ? new Date(value) : undefined) : 
               value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatDateForInput = (date?: Date): string => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      {/* Basic Information */}
      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <h3 className='text-lg font-medium text-slate-900 mb-4'>Basic Information</h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label htmlFor='name' className='block text-sm font-medium text-slate-700 mb-1'>
              Supply Name *
            </label>
            <input
              type='text'
              id='name'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder='Enter supply name'
            />
            {errors.name && <p className='mt-1 text-sm text-red-600'>{errors.name}</p>}
          </div>

          <div>
            <label htmlFor='type' className='block text-sm font-medium text-slate-700 mb-1'>
              Type *
            </label>
            <select
              id='type'
              name='type'
              value={formData.type}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              {Object.values(SupplyType).map(type => (
                <option key={type} value={type}>
                  {getSupplyTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='quantity' className='block text-sm font-medium text-slate-700 mb-1'>
              Current Quantity *
            </label>
            <input
              type='number'
              id='quantity'
              name='quantity'
              value={formData.quantity}
              onChange={handleInputChange}
              min='0'
              step='0.01'
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.quantity ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.quantity && <p className='mt-1 text-sm text-red-600'>{errors.quantity}</p>}
          </div>

          <div>
            <label htmlFor='unit' className='block text-sm font-medium text-slate-700 mb-1'>
              Unit *
            </label>
            <select
              id='unit'
              name='unit'
              value={formData.unit}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              {Object.values(SupplyUnit).map(unit => (
                <option key={unit} value={unit}>
                  {getSupplyUnitLabel(unit)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor='unitPrice' className='block text-sm font-medium text-slate-700 mb-1'>
              Unit Price (R) *
            </label>
            <AmountInput
              id='unitPrice'
              name='unitPrice'
              value={formData.unitPrice}
              onChange={(v) => {
                setFormData(prev => ({ ...prev, unitPrice: v ?? 0 }));
                if (errors.unitPrice) setErrors(prev => ({ ...prev, unitPrice: '' }));
              }}
              min={0}
              inputClassName={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.unitPrice ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.unitPrice && <p className='mt-1 text-sm text-red-600'>{errors.unitPrice}</p>}
          </div>

          <div>
            <label htmlFor='supplier' className='block text-sm font-medium text-slate-700 mb-1'>
              Supplier
            </label>
            <input
              type='text'
              id='supplier'
              name='supplier'
              value={formData.supplier || ''}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter supplier name'
            />
          </div>
        </div>
      </div>

      {/* Dates and Tracking */}
      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <h3 className='text-lg font-medium text-slate-900 mb-4'>Dates & Tracking</h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label htmlFor='purchaseDate' className='block text-sm font-medium text-slate-700 mb-1'>
              Purchase Date
            </label>
            <input
              type='date'
              id='purchaseDate'
              name='purchaseDate'
              value={formatDateForInput(formData.purchaseDate)}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label htmlFor='expirationDate' className='block text-sm font-medium text-slate-700 mb-1'>
              Expiration Date
            </label>
            <input
              type='date'
              id='expirationDate'
              name='expirationDate'
              value={formatDateForInput(formData.expirationDate)}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            />
          </div>

          <div>
            <label htmlFor='batchNumber' className='block text-sm font-medium text-slate-700 mb-1'>
              Batch Number
            </label>
            <input
              type='text'
              id='batchNumber'
              name='batchNumber'
              value={formData.batchNumber || ''}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter batch number'
            />
          </div>

          <div>
            <label htmlFor='location' className='block text-sm font-medium text-slate-700 mb-1'>
              Storage Location
            </label>
            <input
              type='text'
              id='location'
              name='location'
              value={formData.location || ''}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter storage location'
            />
          </div>
        </div>
      </div>

      {/* Stock Management */}
      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <h3 className='text-lg font-medium text-slate-900 mb-4'>Stock Management</h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label htmlFor='minimumQuantity' className='block text-sm font-medium text-slate-700 mb-1'>
              Minimum Quantity *
            </label>
            <input
              type='number'
              id='minimumQuantity'
              name='minimumQuantity'
              value={formData.minimumQuantity}
              onChange={handleInputChange}
              min='0'
              step='0.01'
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.minimumQuantity ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.minimumQuantity && <p className='mt-1 text-sm text-red-600'>{errors.minimumQuantity}</p>}
          </div>

          <div>
            <label htmlFor='reorderPoint' className='block text-sm font-medium text-slate-700 mb-1'>
              Reorder Point *
            </label>
            <input
              type='number'
              id='reorderPoint'
              name='reorderPoint'
              value={formData.reorderPoint}
              onChange={handleInputChange}
              min='0'
              step='0.01'
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.reorderPoint ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.reorderPoint && <p className='mt-1 text-sm text-red-600'>{errors.reorderPoint}</p>}
          </div>

          <div>
            <label htmlFor='maxStockLevel' className='block text-sm font-medium text-slate-700 mb-1'>
              Maximum Stock Level
            </label>
            <input
              type='number'
              id='maxStockLevel'
              name='maxStockLevel'
              value={formData.maxStockLevel || ''}
              onChange={handleInputChange}
              min='0'
              step='0.01'
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.maxStockLevel ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.maxStockLevel && <p className='mt-1 text-sm text-red-600'>{errors.maxStockLevel}</p>}
          </div>

          <div>
            <label htmlFor='averageDailyUsage' className='block text-sm font-medium text-slate-700 mb-1'>
              Average Daily Usage
            </label>
            <input
              type='number'
              id='averageDailyUsage'
              name='averageDailyUsage'
              value={formData.averageDailyUsage || ''}
              onChange={handleInputChange}
              min='0'
              step='0.01'
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.averageDailyUsage ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.averageDailyUsage && <p className='mt-1 text-sm text-red-600'>{errors.averageDailyUsage}</p>}
          </div>

          <div>
            <label htmlFor='leadTimeDays' className='block text-sm font-medium text-slate-700 mb-1'>
              Lead Time (Days)
            </label>
            <input
              type='number'
              id='leadTimeDays'
              name='leadTimeDays'
              value={formData.leadTimeDays || 7}
              onChange={handleInputChange}
              min='0'
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.leadTimeDays ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.leadTimeDays && <p className='mt-1 text-sm text-red-600'>{errors.leadTimeDays}</p>}
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <h3 className='text-lg font-medium text-slate-900 mb-4'>Additional Information</h3>
        <div className='space-y-4'>
          <div>
            <label htmlFor='description' className='block text-sm font-medium text-slate-700 mb-1'>
              Description
            </label>
            <textarea
              id='description'
              name='description'
              value={formData.description || ''}
              onChange={handleInputChange}
              rows={3}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter description'
            />
          </div>

          <div>
            <label htmlFor='instructions' className='block text-sm font-medium text-slate-700 mb-1'>
              Usage Instructions
            </label>
            <textarea
              id='instructions'
              name='instructions'
              value={formData.instructions || ''}
              onChange={handleInputChange}
              rows={3}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter usage instructions'
            />
          </div>

          <div>
            <label htmlFor='storageConditions' className='block text-sm font-medium text-slate-700 mb-1'>
              Storage Conditions
            </label>
            <input
              type='text'
              id='storageConditions'
              name='storageConditions'
              value={formData.storageConditions || ''}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='e.g., Store in cool, dry place'
            />
          </div>

          <div>
            <label htmlFor='notes' className='block text-sm font-medium text-slate-700 mb-1'>
              Notes
            </label>
            <textarea
              id='notes'
              name='notes'
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={3}
              className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              placeholder='Enter any additional notes'
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className='flex justify-end space-x-3 pt-6 border-t border-gray-200'>
        <button
          type='button'
          onClick={onCancel}
          className='px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type='submit'
          className='px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          disabled={loading}
        >
          {loading ? 'Saving...' : supply ? 'Update Supply' : 'Create Supply'}
        </button>
      </div>
    </form>
  );
};

export default SupplyForm; 